import React, { useMemo, useRef, forwardRef, useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Typography,
  Divider,
  Box,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useReactToPrint } from "react-to-print";
import Barcode from "react-barcode";
import { ParcelaResumo } from "../../../../Storage/guias";
import { useEntidade } from "../../../../Hooks/EntidadeHook";
import axios from "axios";
import { api_pix } from "../../../../Utils/Urls";
import { formatDate } from "../../../../Utils/Formatacoes";
import Holidays from "date-holidays";

/* ===================== HELPERS ===================== */

const hd = new Holidays("BR");

function isFimDeSemana(date: Date) {
  const d = date.getDay();
  return d === 0 || d === 6;
}

function isFeriado(date: Date) {
  return !!hd.isHoliday(date);
}

function proximoDiaUtil(date: Date) {
  const d = new Date(date);

  while (isFimDeSemana(d) || isFeriado(d)) {
    d.setDate(d.getDate() + 1);
  }

  return d;
}

const formatBRL = (v: any): string =>
  v == null || v === ""
    ? "-"
    : new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(v));

const isGuiaVencida = (vencimento?: string | Date) => {
  if (!vencimento) return false;

  const d =
    vencimento instanceof Date
      ? new Date(vencimento)
      : new Date(String(vencimento).includes("T") ? String(vencimento) : `${vencimento}T00:00:00`);

  d.setDate(d.getDate() + 1);

  if (Number.isNaN(d.getTime())) return false;

  const vencimentoReal = proximoDiaUtil(d);

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return hoje > vencimentoReal;
};

const sanitizeBarcode = (bar?: string) => (bar || "").replace(/\D/g, "");

const pickBarcodeFormat = (digits: string) =>
  /^\d+$/.test(digits) && digits.length % 2 === 0 ? "ITF" : "CODE128";

const getNumeroParcela = (guia: ParcelaResumo) => {
  const n = Number((guia as any)?.parcela);
  return Number.isFinite(n) ? n : null;
};

const getValorNumerico = (guia: ParcelaResumo) => {
  const n = Number((guia as any)?.valor ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const getPixKey = (guia: ParcelaResumo) => guia?.codigoBarras44 || "";

type PixData = {
  pixCopiaECola: string;
  qrBase64: string;
};

/* ===================== PRINT CSS ===================== */

const PrintStyles = () => (
  <style>{`
    @page { size: A4; margin: 12mm; }

    @media print {
      .no-print { display: none !important; }
      .carne-page { page-break-after: always; }
      .carne-page:last-of-type { page-break-after: auto; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .row-2cols { flex-wrap: nowrap !important; }
    }

    .carne-shell {
      border: 1px solid #ddd;
      border-radius: 10px;
      padding: 14px;
    }

    .cut-line { border-top: 1px dashed #bbb; margin: 10px 0 14px; }

    .header-left { display:flex; align-items:center; gap:12px; }
    .pref-logo { height: 48px; width:auto; object-fit: contain; }

    .mono {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace;
    }

    .pix-qr {
      width: 140px;
      height: 140px;
      border: 2px solid #111;
      border-radius: 8px;
      display:flex;
      align-items:center;
      justify-content:center;
      background: #fff;
      overflow: hidden;
      flex-shrink: 0;
    }

    .barcode-box {
      display:flex;
      align-items:center;
      justify-content:center;
      padding-top: 8px;
    }
  `}</style>
);

/* ===================== PRINTABLE ===================== */

interface PrintableProps {
  items: ParcelaResumo[];
  pixMap: Record<string, PixData>;
  pixLoading: boolean;
}

const entidades_sem_pix = [24];

const CarnePrintable = forwardRef<HTMLDivElement, PrintableProps>(
  ({ items, pixMap, pixLoading }, ref) => {
    const { entidade } = useEntidade();


    return (
      <div ref={ref}>
        <PrintStyles />

        {items.map((guia, idx) => {
          const digits = sanitizeBarcode(guia?.codigoBarras44!);
          const vencida = isGuiaVencida(guia.vencimento);
          const pixData = guia?.codigoBarras44 ? pixMap[guia.codigoBarras44] : undefined;

          const valorFormatado = formatBRL((guia as any).valor);

          const descricaoTexto =
            [guia.descr1, guia.descr2, guia.descr3]
              .map((s) => (s ?? "").trim())
              .filter(Boolean)
              .join("\n") || "-";

          const enderecoTexto =
            [guia.endereco, guia.bairro, guia.cidade, guia.uf, guia.cep]
              .filter(Boolean)
              .join(", ") || "-";

          return (
            <Box key={`${guia?.registro ?? "sem-registro"}-${idx}`} className="carne-page">
              <Box className="carne-shell">
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, gap: 2 }}>
                  <Box className="header-left">
                    {entidade.logo ? (
                      <Box component="img" src={entidade.logo} alt="Logo" className="pref-logo" />
                    ) : null}
                    <Box>
                      <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.1 }}>
                        {entidade.titulo || "Município"}
                      </Typography>
                      <Typography variant="subtitle1">Guia de Arrecadação</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" color="text.secondary">
                      Registro
                    </Typography>
                    <Typography fontWeight={900}>
                      {guia.registro ?? "-"}{" "}
                      {guia.parcela !== undefined && guia.parcela !== null
                        ? `— Parc. ${guia.parcela}`
                        : ""}
                    </Typography>
                  </Box>
                </Box>

                <Divider className="cut-line" />

                <Box
                  className="row-2cols"
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    flexWrap: { xs: "wrap", md: "nowrap" },
                  }}
                >
                  <Box
                    sx={{
                      flex: { xs: "1 1 100%", md: "1 1 66%" },
                      minWidth: 0,
                      p: 1.25,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Contribuinte
                    </Typography>
                    <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.15 }}>
                      {guia.descricao || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: { xs: "1 1 100%", md: "1 1 34%" },
                      minWidth: 220,
                      p: 1.25,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Valor
                    </Typography>
                    <Typography variant="h5" fontWeight={900} sx={{ whiteSpace: "nowrap" }}>
                      {valorFormatado}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  className="row-2cols"
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    mt: 1.25,
                    flexWrap: { xs: "wrap", md: "nowrap" },
                  }}
                >
                  <Box
                    sx={{
                      flex: { xs: "1 1 100%", md: "1 1 66%" },
                      minWidth: 0,
                      p: 1.25,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Endereço
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {enderecoTexto}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: { xs: "1 1 100%", md: "1 1 34%" },
                      minWidth: 220,
                      p: 1.25,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Vencimento
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={900}
                      color={vencida ? "error.main" : "text.primary"}
                    >
                      {formatDate(guia.vencimento)}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 1.25,
                    p: 1.25,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.default",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Descrição da Taxa
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {descricaoTexto}
                  </Typography>
                </Box>
                {(
                  entidade.id === 8 ||
                  entidade.banco === "1-B_CACIQUE"
                ) &&
                  guia.orientacoesPagamento?.trim() && (
                    <Box
                      sx={{
                        mt: 1.25,
                        p: 1.25,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.default",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Orientações de Pagamento
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontWeight: 600 }}
                      >
                        {guia.orientacoesPagamento}
                      </Typography>
                    </Box>
                  )}
                <Divider className="cut-line" />

                {guia.obs && guia.obs.trim() !== "" && (
                  <>
                    <Box
                      sx={{
                        mt: 1.25,
                        p: 1.25,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.default",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Observação
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {guia.obs}
                      </Typography>
                    </Box>

                    <Divider className="cut-line" />
                  </>
                )}

                {vencida && (
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      border: 1,
                      borderColor: "warning.main",
                      bgcolor: "warning.light",
                      mb: 1.5,
                    }}
                  >
                    <Typography fontWeight={900}>Guia vencida</Typography>
                    <Typography variant="body2">
                      O pagamento pode não ser aceito. Solicite uma nova via atualizada na
                      Prefeitura.
                    </Typography>
                  </Box>
                )}

                <Typography
                  className="mono"
                  sx={{
                    wordBreak: "break-all",
                    mb: 0.75,
                    fontSize: 14,
                    textAlign: "center",
                  }}
                >
                  {guia.linhaDigitavel || "-"}
                </Typography>

                <Box className="barcode-box" sx={{ mb: 1.5 }}>
                  {digits ? (
                    <Barcode
                      value={digits}
                      format={pickBarcodeFormat(digits)}
                      displayValue={false}
                      height={60}
                      width={2}
                      margin={0}
                      renderer="svg"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      (Sem código de barras)
                    </Typography>
                  )}
                </Box>

                {!vencida && !entidades_sem_pix.includes(entidade.id) && (
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      minHeight: 165,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                      Pagamento via PIX
                    </Typography>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems="flex-start"
                    >
                      <Box className="pix-qr">
                        {pixData?.qrBase64 ? (
                          <img
                            src={pixData.qrBase64}
                            alt="QR Pix"
                            style={{ width: 140, height: 140, objectFit: "contain" }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            className="mono"
                            sx={{ textAlign: "center", px: 1 }}
                          >
                            QR CODE
                            <br />
                            {pixLoading ? "(gerando...)" : "(indisponível)"}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                        <Typography variant="caption" color="text.secondary">
                          Copia e cola (PIX)
                        </Typography>

                        <Box
                          sx={{
                            mt: 0.5,
                            p: 1,
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            bgcolor: "background.default",
                          }}
                        >
                          <Typography
                            variant="body2"
                            className="mono"
                            sx={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}
                          >
                            {pixData?.pixCopiaECola
                              ? pixData.pixCopiaECola
                              : pixLoading
                                ? "Gerando PIX..."
                                : "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </div>
    );
  }
);

CarnePrintable.displayName = "CarnePrintable";

/* ===================== DIALOG ===================== */

interface Props {
  guias?: ParcelaResumo | ParcelaResumo[];
  open: boolean;
  onClose: () => void;
}

type TipoVisualizacao = "UNICA" | "PARCELADA";

export const CarneDialog: React.FC<Props> = ({ guias, open, onClose }) => {
  const { entidade } = useEntidade();

  const allItems = useMemo<ParcelaResumo[]>(() => {
    if (!guias) return [];
    return Array.isArray(guias) ? guias : [guias];
  }, [guias]);

  const parcelasUnica = useMemo(() => {
    return allItems.filter((g) => getNumeroParcela(g) === 0);
  }, [allItems]);

  const parcelasParceladas = useMemo(() => {
    return allItems
      .filter((g) => {
        const parcela = getNumeroParcela(g);
        return parcela !== null && parcela > 0;
      })
      .sort((a, b) => (getNumeroParcela(a) ?? 0) - (getNumeroParcela(b) ?? 0));
  }, [allItems]);

  const temSomenteParcelaZero = parcelasUnica.length > 0 && parcelasParceladas.length === 0;
  const temUnicaEParceladas = parcelasUnica.length > 0 && parcelasParceladas.length > 0;

  const parcelaZeroVencida = useMemo(() => {
    if (parcelasUnica.length === 0) return false;
    return parcelasUnica.every((g) => isGuiaVencida(g.vencimento));
  }, [parcelasUnica]);

  const podeMostrarOpcaoUnica =
    temSomenteParcelaZero || (temUnicaEParceladas && !parcelaZeroVencida);
  const podeMostrarOpcaoParcelada = parcelasParceladas.length > 0;

  const valorUnica = useMemo(
    () => parcelasUnica.reduce((acc, g) => acc + getValorNumerico(g), 0),
    [parcelasUnica]
  );

  const valorParcelado = useMemo(
    () => parcelasParceladas.reduce((acc, g) => acc + getValorNumerico(g), 0),
    [parcelasParceladas]
  );

  const economia = useMemo(() => {
    if (!temUnicaEParceladas) return 0;
    const dif = valorParcelado - valorUnica;
    return dif > 0 ? dif : 0;
  }, [temUnicaEParceladas, valorParcelado, valorUnica]);

  const modoInicial: TipoVisualizacao = useMemo(() => {
    if (podeMostrarOpcaoUnica) return "UNICA";
    return "PARCELADA";
  }, [podeMostrarOpcaoUnica]);

  const [modo, setModo] = useState<TipoVisualizacao>(modoInicial);
  const [pixMap, setPixMap] = useState<Record<string, PixData>>({});
  const [pixLoading, setPixLoading] = useState(false);

  useEffect(() => {
    setModo(modoInicial);
  }, [modoInicial, open, guias]);

  const items = useMemo<ParcelaResumo[]>(() => {
    if (temSomenteParcelaZero) return parcelasUnica;
    if (modo === "UNICA") return parcelasUnica;
    return parcelasParceladas;
  }, [modo, temSomenteParcelaZero, parcelasUnica, parcelasParceladas]);

  const pixFaltantes = useMemo(() => {
    return items.filter((g) => {
      const key = getPixKey(g);
      return !!key && !pixMap[key] && !isGuiaVencida(g.vencimento);
    });
  }, [items, pixMap]);

  const gerarPixParaGuias = useCallback(
    async (guiasParaGerar: ParcelaResumo[]) => {
      if (!entidade?.id || !guiasParaGerar.length) return;

      setPixLoading(true);

      try {
        const resultados = await Promise.all(
          guiasParaGerar.map(async (guia) => {
            const codigo = guia.codigoBarras44;
            if (!codigo || isGuiaVencida(guia.vencimento)) return null;

            try {
              const { data } = await axios.post(`${api_pix}`, {
                id_entidade: entidade.id,
                valor: guia.valor,
                dataDeVencimento: guia.vencimento?.toString().substring(0, 10),
                cod_barras: guia.codigoBarras44,
                nome: guia.descricao,
                cpf_cnpj: guia.cpf || guia.cnpj,
                carne: guia.registro,
              });

              if (!data?.status || !data?.data?.pixCopiaECola || !data?.data?.qrBase64) {
                return null;
              }

              return {
                codigo,
                pixCopiaECola: data.data.pixCopiaECola,
                qrBase64: data.data.qrBase64,
              };
            } catch {
              return null;
            }
          })
        );

        const novos: Record<string, PixData> = {};

        resultados.forEach((r) => {
          if (!r) return;
          novos[r.codigo] = {
            pixCopiaECola: r.pixCopiaECola,
            qrBase64: r.qrBase64,
          };
        });

        if (Object.keys(novos).length > 0) {
          setPixMap((prev) => ({
            ...prev,
            ...novos,
          }));
        }
      } finally {
        setPixLoading(false);
      }
    },
    [entidade?.id]
  );

  const tentarGerarPixFaltantes = useCallback(async () => {
    await gerarPixParaGuias(pixFaltantes);
  }, [gerarPixParaGuias, pixFaltantes]);

  useEffect(() => {
    if (!open || !items.length) return;

    const naoGeradosAinda = items.filter((g) => {
      const key = getPixKey(g);
      return !!key && !pixMap[key] && !isGuiaVencida(g.vencimento);
    });

    if (naoGeradosAinda.length === 0) return;

    gerarPixParaGuias(naoGeradosAinda);
  }, [open, items, pixMap, gerarPixParaGuias]);

  const printRef = useRef<HTMLDivElement>(null);

  const prefeituraTitulo = (entidade.titulo || "Prefeitura").replace(
    "Prefeitura -",
    "Prefeitura Municipal de "
  );

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle:
      items.length > 1
        ? `Carnes_${prefeituraTitulo}_${modo === "UNICA" ? "Parcela_Unica" : "Parcelado"}`
        : "carne-arrecadacao",
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" keepMounted>
      <DialogTitle sx={{ position: "relative", textAlign: "center" }}>
        {items.length > 1 ? "Carnês de Arrecadação" : "Carnê de Arrecadação"}

        <div
          style={{
            color: "red",
            fontWeight: "bold",
            marginTop: "6px",
            animation: "piscar 1s infinite",
          }}
        >
          🚨 VERIFIQUE SEUS DADOS ANTES DE PAGAR 🚨
        </div>

        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
          className="no-print"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <style>
          {`
            @keyframes piscar {
              0% { opacity: 1; }
              50% { opacity: 0; }
              100% { opacity: 1; }
            }
          `}
        </style>
      </DialogTitle>

      <DialogContent dividers>
        {allItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma guia de arrecadação selecionada.
          </Typography>
        ) : (
          <>
            {temUnicaEParceladas && (
              <Box className="no-print" sx={{ mb: 2 }}>
                <Tabs
                  value={modo}
                  onChange={(_, v) => setModo(v)}
                  variant="fullWidth"
                  sx={{
                    mb: 1.5,
                    "& .MuiTab-root": {
                      fontWeight: 900,
                      textTransform: "none",
                    },
                  }}
                >
                  {podeMostrarOpcaoUnica && <Tab value="UNICA" label="Parcela única" />}
                  {podeMostrarOpcaoParcelada && <Tab value="PARCELADA" label="Parcelado" />}
                </Tabs>

                <Box
                  sx={{
                    p: 1.5,
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 2,
                    background: "rgba(0,0,0,0.02)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {podeMostrarOpcaoUnica && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography fontWeight={800}>Parcela única</Typography>
                      <Chip
                        label={formatBRL(valorUnica)}
                        sx={{ fontWeight: 900 }}
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                  )}

                  {podeMostrarOpcaoParcelada && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography fontWeight={800}>Total parcelado</Typography>
                      <Chip
                        label={formatBRL(valorParcelado)}
                        sx={{ fontWeight: 900 }}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  )}

                  {economia > 0 && podeMostrarOpcaoUnica && podeMostrarOpcaoParcelada && (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 1.25,
                        borderRadius: 1.5,
                        background:
                          modo === "UNICA"
                            ? "rgba(46,125,50,0.10)"
                            : "rgba(211,47,47,0.10)",
                        border:
                          modo === "UNICA"
                            ? "1px solid rgba(46,125,50,0.25)"
                            : "1px solid rgba(211,47,47,0.25)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: modo === "UNICA" ? "success.dark" : "error.dark",
                        }}
                      >
                        {modo === "UNICA"
                          ? `Você economizará ${formatBRL(economia)} pagando em parcela única.`
                          : `Você deixará de economizar ${formatBRL(
                            economia
                          )} escolhendo o parcelamento.`}
                      </Typography>
                    </Box>
                  )}

                  {parcelaZeroVencida && temUnicaEParceladas && (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 1.25,
                        borderRadius: 1.5,
                        background: "rgba(237,108,2,0.10)",
                        border: "1px solid rgba(237,108,2,0.25)",
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, color: "warning.dark" }}>
                        A parcela única venceu, então apenas a opção parcelada está disponível.
                      </Typography>
                    </Box>
                  )}

                  {pixFaltantes.length > 0 && (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 1.25,
                        borderRadius: 1.5,
                        background: "rgba(237,108,2,0.10)",
                        border: "1px solid rgba(237,108,2,0.25)",
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, color: "warning.dark" }}>
                        Alguns PIX não foram gerados automaticamente para esta visualização.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {temSomenteParcelaZero && (
              <Box className="no-print" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 2,
                    background: "rgba(0,0,0,0.02)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography fontWeight={900}>Parcela única</Typography>
                  <Chip
                    label={formatBRL(valorUnica)}
                    sx={{ fontWeight: 900 }}
                    color="success"
                    variant="outlined"
                  />
                </Box>

                {pixFaltantes.length > 0 && (
                  <Box
                    sx={{
                      mt: 1.25,
                      p: 1.25,
                      borderRadius: 1.5,
                      background: "rgba(237,108,2,0.10)",
                      border: "1px solid rgba(237,108,2,0.25)",
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, color: "warning.dark" }}>
                      Alguns PIX não foram gerados automaticamente para esta visualização.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <div ref={printRef}>
              <CarnePrintable items={items} pixMap={pixMap} pixLoading={pixLoading} />
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: 2,
          py: 1.5,
        }}
        className="no-print"
      >
        <Button onClick={onClose} variant="text">
          Cancelar
        </Button>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {pixFaltantes.length > 0 && !entidades_sem_pix.includes(entidade.id) && (
            <Button
              onClick={tentarGerarPixFaltantes}
              variant="contained"
              color="warning"
              startIcon={<AutorenewIcon />}
              disabled={pixLoading}
            >
              {pixLoading
                ? "Gerando PIX..."
                : `Tentar gerar PIX faltantes (${pixFaltantes.length})`}
            </Button>
          )}

          <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />}>
            Imprimir
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};